import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange, DateRangePicker, PickersShortcutsItem } from '@mui/x-date-pickers-pro';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import { useGetCarouselMetrics } from '~/hooks/apis/experiences';

import { CarouselMetric, GeoFilter, OnDemandCarouselMetric, getCarouselMetrics } from '~/services/ExperiencesService';
import { Dayjs, getDayJs } from '~/services/TimeService';
import { customerPortalHost } from '~/services/common';

import { formatCurrency, formatNumber } from '../utils';

type CountryOption = { label: string; iso2: string };
const COUNTRY_OPTIONS: Array<CountryOption> = getCountryObjs()
  .map((c) => ({ label: c.countryName, iso2: c.iso2 }))
  .sort((a, b) => a.label.localeCompare(b.label));

const DATE_RANGE_SHORTCUTS: Array<PickersShortcutsItem<DateRange<Dayjs>>> = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(7, 'day'), today.subtract(1, 'day')];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(30, 'day'), today.subtract(1, 'day')];
    },
  },
  {
    label: 'Last 90 Days',
    getValue: () => {
      const today = getDayJs();
      return [today.subtract(90, 'day'), today.subtract(1, 'day')];
    },
  },
];

const CAROUSEL_DISPLAY_NAMES: Record<string, string> = {
  'OfferList - EXPERIENCES - Recently Viewed Carousel': 'Recently Viewed',
  'OfferList - EXPERIENCES - Experiences Top Three Carousel': 'Our top 3 experiences',
  'OfferList - EXPERIENCES - YAML Carousel': 'Local discounts exclusive to LuxPlus+ members',
  'OfferList - EXPERIENCES - Experiences Our exclusive experiences Carousel': 'Our exclusive experiences',
  'OfferList - EXPERIENCES - Experiences Top activities for you Carousel': 'Top activities for you',
  'OfferList - EXPERIENCES - Experiences Discover dining experiences Carousel': 'Discover dining experiences',
  'OfferList - EXPERIENCES - Experiences Explore your destination Carousel': 'Explore your destination',
  'OfferList - EXPERIENCES - Collections Carousel - Summer Highlights': 'Summer Highlights',
  'OfferList - EXPERIENCES - Collections Carousel - Things to Do with Kids': 'Things to Do with Kids',
  'OfferList - EXPERIENCES - Collections Carousel - Romantic Experiences': 'Romantic Experiences',
  'OfferList - EXPERIENCES - Collections Carousel - Perfect Gifts for Someone Special':
    'Perfect Gifts for Someone Special',
  'OfferList - EXPERIENCES - Experiences See the sights Carousel': 'See the sights',
  'OfferList - EXPERIENCES - Experiences Live events Carousel': 'Live events',
  'OfferList - EXPERIENCES - Experiences Indulge yourself Carousel': 'Indulge yourself',
};

const CP_CAROUSEL_ORDER = [
  'OfferList - EXPERIENCES - Recently Viewed Carousel',
  'OfferList - EXPERIENCES - Experiences Top Three Carousel',
  'OfferList - EXPERIENCES - YAML Carousel',
  'OfferList - EXPERIENCES - Experiences Our exclusive experiences Carousel',
  'OfferList - EXPERIENCES - Experiences Top activities for you Carousel',
  'OfferList - EXPERIENCES - Experiences Discover dining experiences Carousel',
  'OfferList - EXPERIENCES - Experiences Explore your destination Carousel',
  'OfferList - EXPERIENCES - Collections Carousel - Summer Highlights',
  'OfferList - EXPERIENCES - Collections Carousel - Things to Do with Kids',
  'OfferList - EXPERIENCES - Collections Carousel - Romantic Experiences',
  'OfferList - EXPERIENCES - Collections Carousel - Perfect Gifts for Someone Special',
  'OfferList - EXPERIENCES - Experiences See the sights Carousel',
  'OfferList - EXPERIENCES - Experiences Live events Carousel',
  'OfferList - EXPERIENCES - Experiences Indulge yourself Carousel',
];

export function getDisplayName(carouselName: string): string {
  return CAROUSEL_DISPLAY_NAMES[carouselName] ?? carouselName;
}

export function getCpOrder(carouselName: string): number {
  const index = CP_CAROUSEL_ORDER.indexOf(carouselName);
  return index === -1 ? CP_CAROUSEL_ORDER.length : index;
}

export type SortField = 'cpOrder' | 'carouselName' | 'views' | 'clicks' | 'orders' | 'grossSales' | 'margin';
type SortDirection = 'asc' | 'desc';

type CustomMetricsState = {
  loading: boolean;
  data: Array<OnDemandCarouselMetric> | null;
  error: string | null;
};

export type DisplayRow = {
  carouselName: string;
  displayName: string;
  cpOrder: number;
  views: number;
  clicks: number;
  orders: number;
  grossSalesAud: number;
  marginAud: number;
  daysWithViews: number;
};

export function toDisplayRow(metric: CarouselMetric): DisplayRow {
  const m = metric.metrics7d?.last7d;
  return {
    carouselName: metric.carouselName,
    displayName: getDisplayName(metric.carouselName),
    cpOrder: getCpOrder(metric.carouselName),
    views: m?.sessionsWithViews ?? 0,
    clicks: m?.clicks ?? 0,
    orders: m?.orders ?? 0,
    grossSalesAud: m?.grossSalesAud ?? 0,
    marginAud: m?.marginAud ?? 0,
    daysWithViews: m?.daysWithViews ?? 0,
  };
}

export function toDisplayRowFromOnDemand(metric: OnDemandCarouselMetric): DisplayRow {
  return {
    carouselName: metric.carouselName,
    displayName: getDisplayName(metric.carouselName),
    cpOrder: getCpOrder(metric.carouselName),
    views: metric.metrics.sessionsWithViews,
    clicks: metric.metrics.clicks,
    orders: metric.metrics.orders,
    grossSalesAud: metric.metrics.grossSalesAud,
    marginAud: metric.metrics.marginAud,
    daysWithViews: metric.metrics.daysWithViews,
  };
}

export function getSortValue(row: DisplayRow, field: SortField): number | string {
  switch (field) {
    case 'cpOrder':
      return row.cpOrder;
    case 'carouselName':
      return row.displayName;
    case 'views':
      return row.views;
    case 'clicks':
      return row.clicks;
    case 'orders':
      return row.orders;
    case 'grossSales':
      return row.grossSalesAud;
    case 'margin':
      return row.marginAud;
    default:
      return 0;
  }
}

export default function CarouselMetricsTab() {
  const { data: metrics = [], isLoading, error } = useGetCarouselMetrics();
  const tenant = useSelector((state: App.State) => state.tenant);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('cpOrder');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [geoFilter, setGeoFilter] = useState<GeoFilter>({});
  const [customMetrics, setCustomMetrics] = useState<CustomMetricsState | null>(null);

  const fetchRequestRef = useRef(0);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (startDate: string, endDate: string, geo: GeoFilter) => {
        const requestId = ++fetchRequestRef.current;
        setCustomMetrics({ loading: true, data: null, error: null });

        try {
          const results = await getCarouselMetrics(startDate, endDate, geo);
          if (fetchRequestRef.current !== requestId) {
            return;
          }
          setCustomMetrics({ loading: false, data: results, error: null });
        } catch (err) {
          if (fetchRequestRef.current !== requestId) {
            return;
          }
          setCustomMetrics({
            loading: false,
            data: null,
            error: 'Failed to fetch custom range metrics',
          });
        }
      }, 800),
    [],
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const triggerFetch = useCallback(
    (range: DateRange<Dayjs>, geo: GeoFilter) => {
      const [start, end] = range;
      if (!start || !end || !start.isValid() || !end.isValid() || end.isBefore(start)) {
        debouncedFetch.cancel();
        setCustomMetrics(null);
        return;
      }
      debouncedFetch(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'), geo);
    },
    [debouncedFetch],
  );

  const handleDateRangeChange = useCallback(
    (newRange: DateRange<Dayjs>) => {
      setDateRange(newRange);
      triggerFetch(newRange, geoFilter);
    },
    [triggerFetch, geoFilter],
  );

  const handleCountryChange = useCallback(
    (_: unknown, value: CountryOption | null) => {
      setSelectedCountry(value);
      const nextGeo = { ...geoFilter, country: value?.iso2 };
      setGeoFilter(nextGeo);
      triggerFetch(dateRange, nextGeo);
    },
    [triggerFetch, dateRange, geoFilter],
  );

  const handleRegionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextGeo = { ...geoFilter, region: e.target.value || undefined };
      setGeoFilter(nextGeo);
      triggerFetch(dateRange, nextGeo);
    },
    [triggerFetch, dateRange, geoFilter],
  );

  const handleCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextGeo = { ...geoFilter, city: e.target.value || undefined };
      setGeoFilter(nextGeo);
      triggerFetch(dateRange, nextGeo);
    },
    [triggerFetch, dateRange, geoFilter],
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const isCustomRange = customMetrics?.data != null;

  const displayRows: Array<DisplayRow> = useMemo(() => {
    if (customMetrics?.data) {
      return customMetrics.data.map(toDisplayRowFromOnDemand);
    }
    return metrics.map(toDisplayRow);
  }, [customMetrics?.data, metrics]);

  const filtered = useMemo(
    () =>
      displayRows.filter(
        (row) =>
          !searchTerm.trim() ||
          row.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.carouselName.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [displayRows, searchTerm],
  );

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal as string) : aVal - (bVal as number);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDirection]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error instanceof Error ? error.message : 'Failed to fetch carousel metrics'}</Alert>
    );
  }

  if (metrics.length === 0 && !customMetrics?.data) {
    return <Alert severity="info">No carousel metrics available yet. Data syncs daily at 6am Sydney.</Alert>;
  }

  const updatedAt = metrics[0]?.updatedAt
    ? new Date(metrics[0].updatedAt).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })
    : 'N/A';

  const geoLabels = [selectedCountry?.label, geoFilter.region, geoFilter.city].filter(Boolean).join(' | ');
  const rangeLabel = isCustomRange ? `Custom Range${geoLabels ? ` | ${geoLabels}` : ''}` : 'Last 7 Days (cached)';

  return (
    <Stack direction="column" spacing={3}>
      <Typography variant="h5" fontWeight="bold">
        Carousel Metrics
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <DateRangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          maxDate={getDayJs().subtract(1, 'day')}
          localeText={{ start: 'Start date', end: 'End date' }}
          slotProps={{
            shortcuts: { items: DATE_RANGE_SHORTCUTS },
            textField: { size: 'small' },
          }}
        />
        <Autocomplete
          options={COUNTRY_OPTIONS}
          value={selectedCountry}
          onChange={handleCountryChange}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(opt, val) => opt.iso2 === val.iso2}
          renderInput={(params) => <TextField {...params} label="Country" size="small" />}
          sx={{ minWidth: 200 }}
          disabled={!dateRange[0] || !dateRange[1]}
          clearOnEscape
        />
        <TextField
          label="State / Region"
          placeholder="e.g. New South Wales"
          size="small"
          value={geoFilter.region ?? ''}
          onChange={handleRegionChange}
          disabled={!dateRange[0] || !dateRange[1]}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label="City"
          placeholder="e.g. Sydney"
          size="small"
          value={geoFilter.city ?? ''}
          onChange={handleCityChange}
          disabled={!dateRange[0] || !dateRange[1]}
          sx={{ minWidth: 150 }}
        />
        {customMetrics?.loading && <CircularProgress size={20} />}
      </Stack>

      {customMetrics?.error && <Alert severity="error">{customMetrics.error}</Alert>}

      <Stack direction="row" spacing={3} alignItems="center">
        <Box maxWidth={400} flexGrow={1}>
          <TextField
            fullWidth
            label="Search carousels"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {sorted.length} carousel{sorted.length !== 1 ? 's' : ''} | {rangeLabel}
          {!isCustomRange && ` | Last sync: ${updatedAt}`}
        </Typography>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <SortableHeader
              field="cpOrder"
              label="#"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHeader
              field="carouselName"
              label="Carousel"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHeader
              field="views"
              label="Views"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="clicks"
              label="Clicks"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="orders"
              label="Orders"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="grossSales"
              label="Gross Sales (AUD)"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="margin"
              label="Margin (AUD)"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <TableCell align="right">
              <Typography fontWeight="bold" variant="body2">
                Days w/ Views
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography fontWeight="bold" variant="body2">
                Link
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row.carouselName} hover>
              <TableCell align="center">
                <Typography variant="body2" color="text.secondary">
                  {row.cpOrder + 1}
                </Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={row.carouselName} placement="top-start">
                  <Typography variant="body2" fontWeight="medium">
                    {row.displayName}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="right">{formatNumber(row.views)}</TableCell>
              <TableCell align="right">{formatNumber(row.clicks)}</TableCell>
              <TableCell align="right">
                <Typography fontWeight="bold">{formatNumber(row.orders)}</Typography>
              </TableCell>
              <TableCell align="right">{formatCurrency(row.grossSalesAud)}</TableCell>
              <TableCell align="right">{formatCurrency(row.marginAud)}</TableCell>
              <TableCell align="right">{row.daysWithViews}</TableCell>
              <TableCell align="center">
                <Tooltip title={`Open ${row.carouselName} on CP`}>
                  <IconButton
                    size="small"
                    href={`${customerPortalHost(tenant)}/experiences?carousel=${encodeURIComponent(row.carouselName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function SortableHeader({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  return (
    <TableCell align={align}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'desc'}
        onClick={() => onSort(field)}
      >
        <Typography fontWeight="bold" variant="body2">
          {label}
        </Typography>
      </TableSortLabel>
    </TableCell>
  );
}
